import React from 'react';
import { Card, CardContent, CardActionArea, Typography, Box, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import NotesIcon from '@mui/icons-material/Notes';

/**
 * TestHistoryCard - Displays a summary card for a single test result
 * @param {Object} result - Test result object
 * @param {Function} onClick - Callback when card is clicked
 */
export default function TestHistoryCard({ result, onClick }) {
  // Format timestamp range
  const formatTimeRange = (startMs, endMs) => {
    if (!startMs || !endMs) return 'N/A';
    const startDate = new Date(startMs);
    const endDate = new Date(endMs);
    const dateStr = startDate.toLocaleDateString();
    const startTime = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} ${startTime} - ${endTime}`;
  };

  // Determine status color based on pass rate
  const getStatusColor = (passRate) => {
    if (passRate === 100) return 'success';
    if (passRate >= 80) return 'warning';
    return 'error';
  };

  // Use default values for missing fields
  const passRate = result.pass_rate ?? 0;
  const statusColor = getStatusColor(passRate);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
      }}
    >
      <CardActionArea
        onClick={() => onClick(result)}
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardContent sx={{ width: '100%', flexGrow: 1 }}>
          {/* Header with Flow Name */}
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
            {result.flow_name}
          </Typography>

          {/* Test Case Name */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {result.test_case_name}
          </Typography>

          {/* Pass Rate Badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Chip
              label={`${passRate.toFixed(1)}% Pass Rate`}
              color={statusColor}
              size="small"
              icon={
                passRate === 100 ? (
                  <CheckCircleIcon />
                ) : passRate >= 80 ? (
                  <WarningIcon />
                ) : (
                  <ErrorIcon />
                )
              }
              sx={{ fontWeight: 'bold' }}
            />
          </Box>

          {/* Stats Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
              mb: 2,
              backgroundColor: 'custom.surfaceAlt',
              p: 1.5,
              borderRadius: 1,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Events
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {result.total_events ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Passed
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="success.main">
                {result.passed_events ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Failed
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="error.main">
                {result.failed_events ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Missing
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="warning.main">
                {result.missing_events ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Not Present
              </Typography>

              <Typography variant="body2" fontWeight="bold" color="info.main">
                {result.not_present_in_app ?? 0}
              </Typography>
            </Box>
          </Box>

          {/* Device and Market Info */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={result.device_name || 'Unknown Device'} size="small" variant="outlined" />
            <Chip label={result.market || 'Unknown Market'} size="small" variant="outlined" />
            {/* Notes Indicator */}
            {result.notes && result.notes.trim() !== '' && (
              <Chip
                icon={<NotesIcon />}
                label="Notes"
                size="small"
                sx={{
                  bgcolor: 'action.hover',
                  color: 'primary.main',
                  fontWeight: '600',
                  '& .MuiChip-icon': {
                    color: 'primary.main',
                  },
                }}
              />
            )}
          </Box>

          {/* User Email */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            User: {result.user_email || 'Unknown'}
          </Typography>

          {/* Event Time Range */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Event Time Range: {formatTimeRange(result.timestamp_start, result.timestamp_end)}
          </Typography>

          {/* Executed At */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Executed At: {result.created_at ? new Date(result.created_at).toLocaleString() : 'N/A'}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
